import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsApi, EventFormData, calendarApi, CalendarEvent, CustomField } from '../services/api';
import {
    addDays,
    startOfWeek,
    format,
    setHours,
    setMinutes,
    isSameDay,
    parseISO,
    addMinutes
} from 'date-fns';
import { ArrowLeft, ArrowRight, Clock, Send, Save, Plus, ChevronDown, ChevronRight, Palette, Trash2 } from 'lucide-react';
import { detectTimezone, TIMEZONES } from '../utils/timezones';

const DURATIONS = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
];

const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 7 AM to 6 PM

export function CreateEventPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEditing = !!id;

    // Form state
    const [eventName, setEventName] = useState('');
    const [comments, setComments] = useState('');
    const [description, setDescription] = useState('');
    const [duration, setDuration] = useState(60);
    const [recipients, setRecipients] = useState('');
    const [timezone, setTimezone] = useState(() => detectTimezone());
    const [defaultTheme, setDefaultTheme] = useState<'dark' | 'light' | 'system'>('dark');
    const [customFields, setCustomFields] = useState<CustomField[]>([]);

    // UI state for collapsible sections
    const [expandedSections, setExpandedSections] = useState({
        customFields: false,
        appearance: false
    });

    // Calendar state
    const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
    const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());

    // Outlook calendar availability
    const [busyTimes, setBusyTimes] = useState<CalendarEvent[]>([]);
    const [calendarConnected, setCalendarConnected] = useState(false);
    const [loadingCalendar, setLoadingCalendar] = useState(false);

    // UI state
    const [step, setStep] = useState<1 | 2>(1);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Load existing event if editing
    useEffect(() => {
        if (id) {
            loadEvent(id);
        }
    }, [id]);

    // Load calendar availability when week changes (for step 2)
    useEffect(() => {
        if (step === 2) {
            loadCalendarAvailability();
        }
    }, [weekStart, step]);

    const loadCalendarAvailability = async () => {
        setLoadingCalendar(true);
        try {
            const start = weekStart.toISOString();
            const end = addDays(weekStart, 7).toISOString();
            const result = await calendarApi.getAvailability(start, end);
            setCalendarConnected(result.connected);
            setBusyTimes(result.events);
        } catch (err) {
            console.error('Failed to load calendar:', err);
        } finally {
            setLoadingCalendar(false);
        }
    };

    // Check if a time slot overlaps with any busy time
    const isBusy = (slotTime: Date): { busy: boolean; event?: CalendarEvent } => {
        if (!calendarConnected || busyTimes.length === 0) {
            return { busy: false };
        }

        const slotStart = slotTime.getTime();
        const slotEnd = slotStart + duration * 60 * 1000;

        for (const event of busyTimes) {
            const eventStart = parseISO(event.start).getTime();
            const eventEnd = parseISO(event.end).getTime();

            // Check for overlap
            if (slotStart < eventEnd && slotEnd > eventStart) {
                return { busy: true, event };
            }
        }

        return { busy: false };
    };

    const loadEvent = async (eventId: string) => {
        setIsLoading(true);
        try {
            const event = await eventsApi.get(eventId);
            setEventName(event.event_name);
            setComments(event.comments || '');
            setDescription(event.description || '');
            setDuration(event.duration_minutes);
            setTimezone(event.creator_timezone);
            setDefaultTheme(event.default_theme || 'dark');
            setCustomFields(event.custom_fields || []);

            if (event.recipients) {
                setRecipients(event.recipients.map(r => r.email).join('\n'));
            }

            if (event.time_slots) {
                const slots = new Set(event.time_slots.map(s => s.start_time_utc));
                setSelectedSlots(slots);
            }
        } catch (err: any) {
            alert(err.message || 'Failed to load event');
            navigate('/dashboard');
        } finally {
            setIsLoading(false);
        }
    };

    // Parse recipients from text
    const parseRecipients = (text: string): string[] => {
        return text
            .split(/[,;\n]+/)
            .map(e => e.trim().toLowerCase())
            .filter(e => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
            .filter((e, i, arr) => arr.indexOf(e) === i); // Remove duplicates
    };

    // Validate step 1
    const validateStep1 = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!eventName.trim()) {
            newErrors.eventName = 'Event name is required';
        }

        const emails = parseRecipients(recipients);
        if (emails.length === 0) {
            newErrors.recipients = 'At least one valid email is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle slot selection - store as local datetime string (matching scheduling links convention)
    const toggleSlot = (dateTime: Date) => {
        // TIMEZONE CONVENTION:
        // Store time as local datetime in creator's selected timezone (no UTC conversion)
        // The field is named 'start_time_utc' for API compatibility, but contains LOCAL time
        // This matches the scheduling links convention throughout the app
        // When recipients view the poll, times are converted from creator's timezone to their timezone
        const localTime = format(dateTime, "yyyy-MM-dd'T'HH:mm:ss");

        setSelectedSlots(prev => {
            const next = new Set(prev);
            if (next.has(localTime)) {
                next.delete(localTime);
            } else {
                next.add(localTime);
            }
            return next;
        });
    };

    // Check if a slot is the START of a selected meeting
    const isSlotStart = (dateTime: Date): boolean => {
        const localTime = format(dateTime, "yyyy-MM-dd'T'HH:mm:ss");
        return selectedSlots.has(localTime);
    };

    // Check if a slot is covered by any selected meeting (visual indicator)
    // This shows the full duration of the meeting, not just the start time
    const isSlotCovered = (dateTime: Date): boolean => {
        const slotTime = dateTime.getTime();

        // Check each selected slot to see if this slot falls within its duration
        for (const startTimeStr of selectedSlots) {
            const startTime = parseISO(startTimeStr).getTime();
            const endTime = startTime + duration * 60 * 1000;

            // If this slot is at or after the start, and before the end
            if (slotTime >= startTime && slotTime < endTime) {
                return true;
            }
        }
        return false;
    };

    // Generate week days
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    // Custom fields management
    const addCustomField = () => {
        const newField: CustomField = {
            field_type: 'text',
            field_label: '',
            field_name: `field_${Date.now()}`,
            is_required: false,
            display_order: customFields.length,
            placeholder: ''
        };
        setCustomFields([...customFields, newField]);
    };

    const updateCustomField = (index: number, updates: Partial<CustomField>) => {
        const updated = [...customFields];
        updated[index] = { ...updated[index], ...updates };
        setCustomFields(updated);
    };

    const removeCustomField = (index: number) => {
        setCustomFields(customFields.filter((_, i) => i !== index));
    };

    const toggleSection = (section: keyof typeof expandedSections) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Handle form submission
    const handleSubmit = async (send: boolean = false) => {
        if (selectedSlots.size === 0) {
            alert('Please select at least one time slot');
            return;
        }

        setIsSaving(true);
        try {
            const emails = parseRecipients(recipients);

            const data: EventFormData = {
                event_name: eventName,
                comments: comments || undefined,
                description: description || undefined,
                duration_minutes: duration,
                default_theme: defaultTheme,
                timezone, // Creator's timezone - this is the source of truth for time slots
                time_slots: Array.from(selectedSlots).map(start => {
                    const startDate = parseISO(start);
                    const endDate = addMinutes(startDate, duration);
                    return {
                        // Times are stored in creator's timezone (field named 'start_time_utc' for API compatibility)
                        start_time_utc: start,
                        end_time_utc: format(endDate, "yyyy-MM-dd'T'HH:mm:ss")
                    };
                }),
                recipients: emails,
                custom_fields: customFields.filter(f => f.field_label.trim()),
            };

            let eventId = id;

            if (isEditing) {
                await eventsApi.update(id!, data);
            } else {
                const created = await eventsApi.create(data);
                eventId = created.id;
            }

            if (send && eventId) {
                await eventsApi.send(eventId);
            }

            navigate('/dashboard');
        } catch (err: any) {
            alert(err.message || 'Failed to save event');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="page container">
            <h1 style={{ marginBottom: 'var(--space-6)' }}>
                {isEditing ? 'Edit Meeting Poll' : 'Create New Meeting Poll'}
            </h1>

            {/* Step Indicator */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-4)',
                marginBottom: 'var(--space-6)',
                paddingBottom: 'var(--space-4)',
                borderBottom: '1px solid var(--color-gray-200)'
            }}>
                <button
                    onClick={() => setStep(1)}
                    className={`btn ${step === 1 ? 'btn-primary' : 'btn-secondary'}`}
                >
                    1. Poll Details
                </button>
                <button
                    onClick={() => validateStep1() && setStep(2)}
                    className={`btn ${step === 2 ? 'btn-primary' : 'btn-secondary'}`}
                >
                    2. Select Times
                </button>
            </div>

            {/* Step 1: Event Details */}
            {step === 1 && (
                <div className="card" style={{ maxWidth: '700px' }}>
                    {/* Basic Info */}
                    <div className="form-group">
                        <label className="form-label">Meeting Poll Name *</label>
                        <input
                            type="text"
                            className={`form-input ${errors.eventName ? 'error' : ''}`}
                            value={eventName}
                            onChange={(e) => setEventName(e.target.value)}
                            placeholder="e.g., Q1 Planning Meeting"
                        />
                        {errors.eventName && <p className="form-error">{errors.eventName}</p>}
                    </div>

                    <div className="form-group">
                        <label className="form-label">Description (optional)</label>
                        <textarea
                            className="form-textarea"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Provide details about the meeting agenda, objectives, or any context that will help recipients..."
                            rows={4}
                        />
                        <p className="form-hint">This will be shown to recipients when they vote on times</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                        <div className="form-group">
                            <label className="form-label">Duration</label>
                            <select
                                className="form-select"
                                value={duration}
                                onChange={(e) => setDuration(Number(e.target.value))}
                            >
                                {DURATIONS.map(d => (
                                    <option key={d.value} value={d.value}>{d.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Timezone</label>
                            <select
                                className="form-select"
                                value={timezone}
                                onChange={(e) => setTimezone(e.target.value)}
                            >
                                {TIMEZONES.map(tz => (
                                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Recipients *</label>
                        <textarea
                            className={`form-textarea ${errors.recipients ? 'error' : ''}`}
                            value={recipients}
                            onChange={(e) => setRecipients(e.target.value)}
                            placeholder="Enter email addresses separated by comma, semicolon, or new line..."
                            rows={4}
                        />
                        {errors.recipients && <p className="form-error">{errors.recipients}</p>}
                        <p className="form-hint">
                            {parseRecipients(recipients).length > 0
                                ? `${parseRecipients(recipients).length} valid email(s) detected`
                                : 'Paste multiple emails at once'}
                        </p>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Internal Notes (optional)</label>
                        <textarea
                            className="form-textarea"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Private notes (not visible to recipients)..."
                            rows={2}
                        />
                    </div>

                    {/* Custom Fields Section */}
                    <div style={{ marginTop: 'var(--space-5)', borderTop: '1px solid var(--color-gray-200)', paddingTop: 'var(--space-4)' }}>
                        <button
                            type="button"
                            onClick={() => toggleSection('customFields')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                padding: 'var(--space-3)',
                                background: 'var(--color-gray-50)',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontWeight: 500
                            }}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Plus size={18} />
                                Custom Form Fields {customFields.length > 0 && `(${customFields.length})`}
                            </span>
                            {expandedSections.customFields ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </button>

                        {expandedSections.customFields && (
                            <div style={{ marginTop: 'var(--space-3)', paddingLeft: 'var(--space-3)' }}>
                                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)', marginBottom: 'var(--space-3)' }}>
                                    Collect additional information from recipients (phone, company, etc.)
                                </p>
                                {customFields.map((field, index) => (
                                    <div key={index} style={{ 
                                        marginBottom: 'var(--space-3)', 
                                        padding: 'var(--space-3)', 
                                        background: 'var(--color-gray-50)', 
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-gray-200)'
                                    }}>
                                        <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
                                            <input
                                                type="text"
                                                className="form-input"
                                                placeholder="Field label (e.g., Phone Number)"
                                                value={field.field_label}
                                                onChange={(e) => updateCustomField(index, { field_label: e.target.value })}
                                                style={{ flex: 1 }}
                                            />
                                            <select
                                                className="form-select"
                                                value={field.field_type}
                                                onChange={(e) => updateCustomField(index, { field_type: e.target.value as any })}
                                                style={{ width: '140px' }}
                                            >
                                                <option value="text">Text</option>
                                                <option value="email">Email</option>
                                                <option value="phone">Phone</option>
                                                <option value="textarea">Long Text</option>
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => removeCustomField(index)}
                                                className="btn btn-ghost btn-sm"
                                                style={{ color: 'var(--color-error)' }}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                                            <input
                                                type="text"
                                                className="form-input"
                                                placeholder="Placeholder text (optional)"
                                                value={field.placeholder || ''}
                                                onChange={(e) => updateCustomField(index, { placeholder: e.target.value })}
                                                style={{ flex: 1 }}
                                            />
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={field.is_required}
                                                    onChange={(e) => updateCustomField(index, { is_required: e.target.checked })}
                                                />
                                                Required
                                            </label>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addCustomField}
                                    className="btn btn-secondary btn-sm"
                                >
                                    <Plus size={16} />
                                    Add Field
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Appearance Section */}
                    <div style={{ marginTop: 'var(--space-4)', borderTop: '1px solid var(--color-gray-200)', paddingTop: 'var(--space-4)' }}>
                        <button
                            type="button"
                            onClick={() => toggleSection('appearance')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                padding: 'var(--space-3)',
                                background: 'var(--color-gray-50)',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontWeight: 500
                            }}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Palette size={18} />
                                Appearance
                            </span>
                            {expandedSections.appearance ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </button>

                        {expandedSections.appearance && (
                            <div style={{ marginTop: 'var(--space-3)', paddingLeft: 'var(--space-3)' }}>
                                <label className="form-label">Response Page Theme</label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-2)' }}>
                                    {(['dark', 'light', 'system'] as const).map(theme => (
                                        <label key={theme} style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-3)',
                                            border: `2px solid ${defaultTheme === theme ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                            borderRadius: 'var(--radius-md)',
                                            cursor: 'pointer',
                                            background: defaultTheme === theme ? 'var(--color-primary-light)' : 'transparent'
                                        }}>
                                            <input
                                                type="radio"
                                                name="theme"
                                                value={theme}
                                                checked={defaultTheme === theme}
                                                onChange={(e) => setDefaultTheme(e.target.value as any)}
                                                style={{ margin: 0 }}
                                            />
                                            <span style={{ textTransform: 'capitalize', fontSize: 'var(--font-size-sm)' }}>{theme}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => validateStep1() && setStep(2)}
                        className="btn btn-primary"
                        style={{ marginTop: 'var(--space-5)' }}
                    >
                        Continue to Time Selection
                        <ArrowRight size={18} />
                    </button>
                </div>
            )}

            {/* Step 2: Time Selection */}
            {step === 2 && (
                <div>
                    {/* Duration & Week Navigation */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 'var(--space-4)',
                        flexWrap: 'wrap',
                        gap: 'var(--space-4)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                            <Clock size={18} />
                            <select
                                className="form-select"
                                value={duration}
                                onChange={(e) => setDuration(Number(e.target.value))}
                                style={{ width: 'auto' }}
                            >
                                {DURATIONS.map(d => (
                                    <option key={d.value} value={d.value}>{d.label}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setWeekStart(addDays(weekStart, -7))}
                            >
                                <ArrowLeft size={18} />
                            </button>
                            <span style={{ minWidth: '200px', textAlign: 'center', fontWeight: 500 }}>
                                {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
                            </span>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setWeekStart(addDays(weekStart, 7))}
                            >
                                <ArrowRight size={18} />
                            </button>
                        </div>

                        <div style={{
                            background: 'var(--color-gray-100)',
                            padding: 'var(--space-2) var(--space-3)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            {selectedSlots.size} slot{selectedSlots.size !== 1 ? 's' : ''} selected
                        </div>
                    </div>

                    {/* Outlook Calendar Legend */}
                    {calendarConnected && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-4)',
                            marginBottom: 'var(--space-3)',
                            padding: 'var(--space-3)',
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid #0ea5e9',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <span style={{ color: '#0078d4', fontWeight: 600 }}>📅 Outlook Connected</span>
                                {loadingCalendar && <span style={{ color: 'var(--color-gray-500)' }}>(loading...)</span>}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: 'repeating-linear-gradient(45deg, #fee2e2, #fee2e2 2px, #fecaca 2px, #fecaca 4px)',
                                    borderRadius: '2px',
                                    border: '1px solid #fca5a5'
                                }} />
                                <span>= Busy in Outlook</span>
                            </div>
                        </div>
                    )}

                    {/* Calendar Grid */}
                    <div className="card" style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '80px', padding: 'var(--space-3)' }}></th>
                                    {weekDays.map(day => (
                                        <th
                                            key={day.toISOString()}
                                            style={{
                                                padding: 'var(--space-3)',
                                                textAlign: 'center',
                                                background: isSameDay(day, new Date()) ? 'var(--color-primary-light)' : 'var(--color-gray-50)'
                                            }}
                                        >
                                            <div style={{ fontWeight: 600 }}>{format(day, 'EEE')}</div>
                                            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                                {format(day, 'M/d')}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {HOURS.map(hour => (
                                    <>
                                        {/* Full hour row */}
                                        <tr key={`${hour}:00`}>
                                            <td style={{
                                                padding: 'var(--space-2)',
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--color-gray-500)',
                                                textAlign: 'right',
                                                verticalAlign: 'top'
                                            }}>
                                                {format(setMinutes(setHours(new Date(), hour), 0), 'h:mm a')}
                                            </td>
                                            {weekDays.map(day => {
                                                const slotTime = setMinutes(setHours(day, hour), 0);
                                                const isCovered = isSlotCovered(slotTime);
                                                const isStart = isSlotStart(slotTime);
                                                const busyCheck = isBusy(slotTime);

                                                return (
                                                    <td
                                                        key={slotTime.toISOString()}
                                                        onClick={() => toggleSlot(slotTime)}
                                                        style={{
                                                            padding: '2px',
                                                            cursor: 'pointer'
                                                        }}
                                                        title={busyCheck.busy ? `Busy: ${busyCheck.event?.subject || 'Meeting'}` : undefined}
                                                    >
                                                        <div style={{
                                                            height: '32px',
                                                            background: isCovered
                                                                ? 'var(--color-primary)'
                                                                : busyCheck.busy
                                                                    ? 'repeating-linear-gradient(45deg, #fee2e2, #fee2e2 4px, #fecaca 4px, #fecaca 8px)'
                                                                    : 'var(--color-gray-100)',
                                                            borderRadius: 'var(--radius-sm)',
                                                            transition: 'all var(--transition-fast)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: isCovered ? 'white' : busyCheck.busy ? '#dc2626' : 'transparent',
                                                            fontSize: 'var(--font-size-xs)',
                                                            border: busyCheck.busy && !isCovered ? '1px solid #fca5a5' : 'none'
                                                        }}>
                                                            {isStart ? '✓' : isCovered ? '' : busyCheck.busy ? '•' : ''}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                        {/* Half hour row */}
                                        <tr key={`${hour}:30`}>
                                            <td style={{
                                                padding: 'var(--space-2)',
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-gray-400)',
                                                textAlign: 'right',
                                                verticalAlign: 'top'
                                            }}>
                                                :30
                                            </td>
                                            {weekDays.map(day => {
                                                const slotTime = setMinutes(setHours(day, hour), 30);
                                                const isCovered = isSlotCovered(slotTime);
                                                const isStart = isSlotStart(slotTime);
                                                const busyCheck = isBusy(slotTime);

                                                return (
                                                    <td
                                                        key={slotTime.toISOString()}
                                                        onClick={() => toggleSlot(slotTime)}
                                                        style={{
                                                            padding: '2px',
                                                            cursor: 'pointer'
                                                        }}
                                                        title={busyCheck.busy ? `Busy: ${busyCheck.event?.subject || 'Meeting'}` : undefined}
                                                    >
                                                        <div style={{
                                                            height: '32px',
                                                            background: isCovered
                                                                ? 'var(--color-primary)'
                                                                : busyCheck.busy
                                                                    ? 'repeating-linear-gradient(45deg, #fee2e2, #fee2e2 4px, #fecaca 4px, #fecaca 8px)'
                                                                    : 'var(--color-gray-50)',
                                                            borderRadius: 'var(--radius-sm)',
                                                            transition: 'all var(--transition-fast)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: isCovered ? 'white' : busyCheck.busy ? '#dc2626' : 'transparent',
                                                            fontSize: 'var(--font-size-xs)',
                                                            border: busyCheck.busy && !isCovered ? '1px solid #fca5a5' : 'none'
                                                        }}>
                                                            {isStart ? '✓' : isCovered ? '' : busyCheck.busy ? '•' : ''}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    </>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Actions */}
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-3)',
                        marginTop: 'var(--space-5)',
                        justifyContent: 'space-between'
                    }}>
                        <button
                            onClick={() => setStep(1)}
                            className="btn btn-secondary"
                        >
                            <ArrowLeft size={18} />
                            Back
                        </button>

                        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                            <button
                                onClick={() => handleSubmit(false)}
                                disabled={isSaving}
                                className="btn btn-secondary"
                            >
                                <Save size={18} />
                                Save as Draft
                            </button>
                            <button
                                onClick={() => handleSubmit(true)}
                                disabled={isSaving || selectedSlots.size === 0}
                                className="btn btn-primary"
                            >
                                <Send size={18} />
                                {isSaving ? 'Sending...' : 'Send Invitations'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
